import { MarkerType } from '@xyflow/react';
import { cloneDeep } from 'es-toolkit';
import { Brick, Workflow } from '../../models';
import { enrichWorkflowBrick } from './workflowBrickCatalog';
import { computeElkLayeredPositions } from './workflowElkAutoLayout';

export const WORKFLOW_START_NODE_ID = '__workflow_start__';

/** Default for {@see editor_layout.graph_options.incoming_merge_default} and unset brick `options.incoming_merge` (Kore). */
export const DEFAULT_WORKFLOW_INCOMING_MERGE = 'any';

/** React Flow edge path helpers selectable in the graph toolbar. */
export const WORKFLOW_EDGE_PATH_STYLES = Object.freeze(['bezier', 'smoothstep', 'straight']);

/** @typedef {'bezier'|'smoothstep'|'straight'} WorkflowEdgePathStyle */

export const DEFAULT_WORKFLOW_EDGE_PATH_STYLE = 'bezier';

/**
 * @param {unknown} raw
 * @returns {WorkflowEdgePathStyle}
 */
export function normalizeWorkflowEdgePathStyle(raw) {
    const v = String(raw || '').toLowerCase().trim();
    // Legacy saved value: treat as regular bezier (visually almost identical on L→R edges).
    if (v === 'simplebezier')
        return DEFAULT_WORKFLOW_EDGE_PATH_STYLE;
    if (WORKFLOW_EDGE_PATH_STYLES.includes(v))
        return /** @type {WorkflowEdgePathStyle} */ (v);
    return DEFAULT_WORKFLOW_EDGE_PATH_STYLE;
}

/**
 * Legacy workflows may store an empty PHP array as `[]`; redux-form needs a plain object.
 *
 * @param {unknown} value
 * @returns {Record<string, string>}
 */
export { normalizeBrickInputsMapping } from './workflowInputsMappingUtils';

/** @typedef {'minutes'|'hours'|'days'} FlowTimingUnit */

export const BRICK_NODE_WIDTH = 220;
/** Approximate brick card height for drop centering (React Flow has no fixed node height). */
export const BRICK_NODE_LAYOUT_HEIGHT = 72;
export const BRICK_NODE_GAP_X = 48;

/** Outer box of the Start node (see `.workflow-start-node` in WorkflowGraphEditor.scss). */
export const WORKFLOW_START_NODE_BOX = 44;

/**
 * Gap between Start and the entry brick.
 * Historically used above the first brick (top-down); auto-layout places Start to the left (RIGHT flow).
 */
export const WORKFLOW_START_ABOVE_GAP = 80;

/** Horizontal gap between Start (right edge) and the entry brick (left edge) for LEFT→RIGHT graphs. */
export const WORKFLOW_START_LEFT_GAP = WORKFLOW_START_ABOVE_GAP;

const TIMING_UNITS = new Set(['minutes', 'hours', 'days']);

/** Side port ids on brick nodes (single `source` handle per side + ConnectionMode.Loose). */
export const FLOW_HANDLE_TOP = 'top';
export const FLOW_HANDLE_RIGHT = 'right';
export const FLOW_HANDLE_BOTTOM = 'bottom';
export const FLOW_HANDLE_LEFT = 'left';

const FLOW_HANDLE_IDS = new Set([FLOW_HANDLE_TOP, FLOW_HANDLE_RIGHT, FLOW_HANDLE_BOTTOM, FLOW_HANDLE_LEFT]);

/**
 * Map persisted / legacy handle ids to the four side ids used by brick nodes.
 * (e.g. xyflow defaults, older `in` / `out`, or compound ids like `out-right`.)
 *
 * @param {unknown} handle_id
 * @returns {string}
 */
export function migrateLegacyFlowHandleId(handle_id) {
    const raw = handle_id != null && handle_id !== '' ? String(handle_id).trim() : '';
    if (!raw)
        return '';
    const h = raw.toLowerCase();
    if (FLOW_HANDLE_IDS.has(h))
        return h;
    if (h === 'in')
        return FLOW_HANDLE_TOP;
    if (h === 'out')
        return FLOW_HANDLE_BOTTOM;
    const side = h.match(/(?:^|-)(top|right|bottom|left)(?:$|-)/);
    if (side)
        return side[1];
    return FLOW_HANDLE_BOTTOM;
}

/** @type {ReadonlySet<string>} */
export const EDGE_CONDITION_OPS = new Set([
    'eq',
    'neq',
    'gte',
    'lte',
    'gt',
    'lt',
    'in_list',
    'not_in_list',
    'exists',
    'not_exists',
    'is_true',
    'is_false',
    'truthy',
]);

/** @param {string} op */
export function edgeConditionOpSkipsValue(op) {
    return op === 'exists' || op === 'not_exists' || op === 'is_true' || op === 'is_false' || op === 'truthy';
}

/**
 * @param {string} raw
 * @param {string} op
 * @returns {unknown|undefined}
 */
export function parseEdgeConditionValue(raw, op) {
    if (raw === '' || raw == null)
        return undefined;
    const s = String(raw).trim();
    if (op === 'in_list' || op === 'not_in_list') {
        try {
            const j = JSON.parse(s);
            if (Array.isArray(j))
                return j;
        } catch (e) {
            /* ignore */
        }
        return s.split(',').map((x) => x.trim()).filter(Boolean);
    }
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
        try {
            return JSON.parse(s);
        } catch (e) {
            /* ignore */
        }
    }
    if (/^-?\d+(\.\d+)?$/.test(s))
        return Number(s);
    if (s === 'true')
        return true;
    if (s === 'false')
        return false;
    if (s === 'null')
        return null;
    return s;
}

/**
 * @param {unknown} raw
 * @returns {{ path?: string, op?: string, value?: unknown }|null}
 */
export function normalizeEdgeCondition(raw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const o = raw;
    const path = typeof o.path === 'string' ? o.path.trim() : '';
    const op = typeof o.op === 'string' ? o.op.trim() : '';
    if (!path || !op || !EDGE_CONDITION_OPS.has(op))
        return null;
    const out = { path, op };
    if (!edgeConditionOpSkipsValue(op) && 'value' in o)
        out.value = o.value;
    return out;
}

const EDGE_COND_LABEL_MAX = 96;

/**
 * @param {unknown} val
 * @param {number} [maxLen]
 * @returns {string}
 */
export function stringifyEdgeCondValueForLabel(val, maxLen = EDGE_COND_LABEL_MAX) {
    if (val === undefined || val === null)
        return '';
    try {
        const s = typeof val === 'object' ? JSON.stringify(val) : String(val);
        return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s;
    } catch (_e) {
        return '';
    }
}

/**
 * Kore / Kronos convention for the foreach “iterate” XOR branch.
 *
 * @param {unknown} condition normalized or raw
 * @returns {boolean}
 */
export function isForeachIterateEdgeCondition(condition) {
    if (condition == null || typeof condition !== 'object' || Array.isArray(condition)) {
        return false;
    }
    if (String(condition.path || '').trim() !== '__foreach_branch__') {
        return false;
    }
    if (String(condition.op || '').trim() !== 'eq') {
        return false;
    }
    const v = condition.value;
    return v === 'iterate' || String(v).trim() === 'iterate';
}

/**
 * Human-readable edge condition (graph + run trace), same shape as Kore {@link WorkflowDag::evaluate_condition}.
 *
 * @param {unknown} condition
 * @param {function(string, object=): string} t i18n `t`
 * @returns {string|null}
 */
export function formatWorkflowEdgeConditionText(condition, t) {
    if (condition == null)
        return null;
    if (typeof condition === 'string') {
        const s = condition.trim();
        return s || null;
    }
    if (typeof condition !== 'object' || Array.isArray(condition))
        return null;
    const path = condition.path;
    const op = condition.op;
    if (!path || !op)
        return null;
    const op_label = t(`workflows.graph.op.${op}`, { defaultValue: op });
    const val_str = stringifyEdgeCondValueForLabel(condition.value);
    if (val_str !== '')
        return `${path} · ${op_label} · ${val_str}`;
    return `${path} · ${op_label}`;
}

/**
 * @param {unknown} raw
 * @returns {{ amount: number, unit: FlowTimingUnit }|null}
 */
export function normalizeEdgeTiming(raw) {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
        return null;
    const amount = Number(raw.amount);
    const unit = raw.unit != null ? String(raw.unit).toLowerCase() : '';
    if (!Number.isFinite(amount) || amount < 0 || !TIMING_UNITS.has(unit))
        return null;
    if (amount === 0)
        return null;
    return { amount, unit: /** @type {FlowTimingUnit} */ (unit) };
}

/**
 * @param {unknown} e
 */
export function normalizeFlowEdge(e) {
    if (!e || typeof e !== 'object')
        return null;
    const source = String(e.source || '');
    const target = String(e.target || '');
    if (!source || !target)
        return null;
    const condition = normalizeEdgeCondition(e.condition);
    const timing = normalizeEdgeTiming(e.timing);
    const label = e.label != null && String(e.label).trim() !== '' ? String(e.label).trim() : undefined;
    const priority = Number.isFinite(Number(e.priority)) ? Number(e.priority) : 0;
    const is_default = Boolean(e.is_default);
    return {
        id: String(e.id || `e-${source}-${target}`),
        source,
        target,
        sourceHandle: e.sourceHandle != null && e.sourceHandle !== ''
            ? migrateLegacyFlowHandleId(e.sourceHandle)
            : FLOW_HANDLE_BOTTOM,
        targetHandle: e.targetHandle != null && e.targetHandle !== ''
            ? migrateLegacyFlowHandleId(e.targetHandle)
            : FLOW_HANDLE_TOP,
        ...(condition ? { condition } : {}),
        ...(timing ? { timing } : {}),
        ...(label ? { label } : {}),
        priority,
        is_default,
    };
}

/**
 * @param {unknown} raw
 * @returns {{
 *   nodes: Record<string, { x: number, y: number }>,
 *   viewport: { x: number, y: number, zoom: number },
 *   flow_edges: object[],
 *   graph_options: { incoming_merge_default?: string },
 * }}
 */
export function normalizeEditorLayout(raw) {
    const el = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    const raw_nodes = el.nodes;
    const nodes = raw_nodes && typeof raw_nodes === 'object' && !Array.isArray(raw_nodes) ? { ...raw_nodes } : {};
    const vp = el.viewport && typeof el.viewport === 'object' ? el.viewport : {};
    const raw_edges = el.flow_edges;
    const flow_edges = Array.isArray(raw_edges)
        ? raw_edges
            .map((e) => normalizeFlowEdge(e))
            .filter(Boolean)
        : [];
    const raw_go = el.graph_options;
    const graph_options = raw_go && typeof raw_go === 'object' && !Array.isArray(raw_go)
        ? { ...raw_go }
        : {};
    if (
        graph_options.incoming_merge_default === undefined
        || graph_options.incoming_merge_default === null
        || String(graph_options.incoming_merge_default).trim() === ''
    ) {
        graph_options.incoming_merge_default = DEFAULT_WORKFLOW_INCOMING_MERGE;
    } else {
        const v = String(graph_options.incoming_merge_default).toLowerCase();
        graph_options.incoming_merge_default = v === 'all' ? 'all' : DEFAULT_WORKFLOW_INCOMING_MERGE;
    }
    graph_options.edge_path_style = normalizeWorkflowEdgePathStyle(graph_options.edge_path_style);
    return {
        nodes,
        viewport: {
            x: Number(vp.x) || 0,
            y: Number(vp.y) || 0,
            zoom: Number(vp.zoom) > 0 ? Number(vp.zoom) : 1,
        },
        flow_edges,
        graph_options,
    };
}

export function sortBricks(bricks) {
    return [...(bricks || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
}

/**
 * React Flow node id (stable for session; server id when present).
 * @param {{ id?: string, _clientLayoutId?: string, position?: number }} brick
 */
export function stableBrickNodeId(brick) {
    if (brick.id)
        return String(brick.id);
    if (brick._clientLayoutId)
        return String(brick._clientLayoutId);
    return `pos-${brick.position ?? 0}`;
}

/**
 * Key used inside editor_layout.nodes (server brick id, or client layout id until saved).
 * @param {{ id?: string, _clientLayoutId?: string }} brick
 * @returns {string|null}
 */
export function layoutStorageKeyForBrick(brick) {
    if (brick?.id)
        return String(brick.id);
    if (brick?._clientLayoutId)
        return String(brick._clientLayoutId);
    return stableBrickNodeId(brick);
}

/**
 * Engine workflows may return bricks without server ids while editor_layout.nodes
 * still uses layout keys (UUID). Assign _clientLayoutId so drag/save and dirty
 * detection work.
 *
 * @param {{ bricks?: unknown[], editor_layout?: object }} workflowLike
 * @returns {{ bricks: unknown[], editor_layout?: object }}
 */
export function hydrateWorkflowBricksLayoutIds(workflowLike) {
    if (!workflowLike)
        return workflowLike;

    let bricks = ensureUniqueBrickStableIds(sortBricks(workflowLike.bricks || []));
    if (bricks.length === 0)
        return { ...workflowLike, bricks };

    const missing_layout_id = bricks.some((brick) => !brick?.id && !brick?._clientLayoutId);
    if (!missing_layout_id) {
        return {
            ...workflowLike,
            bricks,
            editor_layout: reconcileWorkflowGraphReferences(bricks, workflowLike.editor_layout),
        };
    }

    const norm = normalizeEditorLayout(workflowLike.editor_layout);
    const claimed_node_keys = new Set();
    bricks.forEach((brick) => {
        const key = brick?.id || brick?._clientLayoutId;
        if (key)
            claimed_node_keys.add(String(key));
    });

    const orphan_node_keys = Object.keys(norm.nodes || {})
        .filter((key) => key !== WORKFLOW_START_NODE_ID && !claimed_node_keys.has(key));

    const brick_ids = bricks.map((brick) => stableBrickNodeId(brick));
    const flow_order = displayFlowOrderBrickIds(brick_ids, norm.flow_edges);
    const order = flow_order.length > 0 ? flow_order : brick_ids;

    let orphan_index = 0;
    const next_brick_by_stable_id = Object.fromEntries(
        bricks.map((brick) => {
            const raw = brick && typeof brick === 'object' ? { ...brick } : brick;
            return [stableBrickNodeId(brick), raw];
        })
    );

    order.forEach((stable_id) => {
        const brick = next_brick_by_stable_id[stable_id];
        if (!brick || brick.id || brick._clientLayoutId)
            return;

        let layout_key = orphan_node_keys[orphan_index++];
        if (!layout_key) {
            layout_key = typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `cid-${brick.position ?? orphan_index}`;
        }
        brick._clientLayoutId = layout_key;
    });

    bricks.forEach((brick) => {
        const stable_id = stableBrickNodeId(brick);
        const next_brick = next_brick_by_stable_id[stable_id];
        if (!next_brick || next_brick.id || next_brick._clientLayoutId)
            return;

        let layout_key = orphan_node_keys[orphan_index++];
        if (!layout_key) {
            layout_key = typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `cid-${brick.position ?? orphan_index}`;
        }
        next_brick._clientLayoutId = layout_key;
    });

    const hydrated_bricks = bricks.map((brick) => next_brick_by_stable_id[stableBrickNodeId(brick)]);
    return {
        ...workflowLike,
        bricks: hydrated_bricks,
        editor_layout: reconcileWorkflowGraphReferences(hydrated_bricks, workflowLike.editor_layout),
    };
}

/**
 * Align editor_layout.nodes and flow_edges on stable brick node ids.
 * Fixes mixed legacy keys (pos-0 vs client UUID) after engine round-trips.
 *
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 * @returns {object}
 */
/**
 * Stable signature for workflow graph dirty detection (bricks + editor_layout).
 *
 * @param {{ bricks?: unknown[], editor_layout?: object }|null|undefined} workflow
 * @returns {string}
 */
export function computeWorkflowGraphDirtySignature(workflow) {
    if (!workflow)
        return '';

    return JSON.stringify({
        b: sortBricks(workflow.bricks).map((brick) => ({
            i: stableBrickNodeId(brick),
            p: brick.position,
            t: brick.type,
            o: brick.options ?? {},
            m: brick.inputs_mapping ?? {},
        })),
        e: normalizeEditorLayout(workflow.editor_layout),
    });
}

export function reconcileWorkflowGraphReferences(bricks, editor_layout) {
    const sorted = ensureUniqueBrickStableIds(bricks || []);
    const norm = normalizeEditorLayout(editor_layout);
    const legacy_to_stable = new Map();
    const nodes = {};
    const claimed_node_keys = new Set();

    const register_alias = (alias, stable) => {
        if (!alias || alias === WORKFLOW_START_NODE_ID)
            return;
        const key = String(alias);
        if (!legacy_to_stable.has(key))
            legacy_to_stable.set(key, stable);
    };

    sorted.forEach((brick, index) => {
        const stable = stableBrickNodeId(brick);
        const pos_key = `pos-${brick.position ?? index}`;
        const lookup_keys = [
            layoutStorageKeyForBrick(brick),
            stable,
            brick?._clientLayoutId,
            brick?.id,
        ]
            .filter(Boolean)
            .map(String);
        if (stable === pos_key || String(brick?.id || '') === pos_key)
            lookup_keys.push(pos_key);
        const remap_aliases = new Set(
            [
                stable,
                layoutStorageKeyForBrick(brick),
                brick?._clientLayoutId,
                brick?.id,
            ]
                .filter(Boolean)
                .map(String)
        );
        // Only map pos-N when it is this brick's canonical id (not merely its list position).
        if (stable === pos_key || String(brick?.id || '') === pos_key)
            remap_aliases.add(pos_key);

        let coords = null;
        for (const alias of lookup_keys) {
            if (norm.nodes[alias]) {
                coords = norm.nodes[alias];
                claimed_node_keys.add(alias);
                break;
            }
        }

        if (!coords) {
            const orphan_key = Object.keys(norm.nodes || {}).find(
                (key) => key !== WORKFLOW_START_NODE_ID && !claimed_node_keys.has(key)
            );
            if (orphan_key) {
                coords = norm.nodes[orphan_key];
                claimed_node_keys.add(orphan_key);
                register_alias(orphan_key, stable);
            }
        }

        nodes[stable] = coords || defaultPositionForIndex(index);
        remap_aliases.forEach((alias) => register_alias(alias, stable));
    });

    const map_edge_endpoint = (endpoint) => {
        if (endpoint === WORKFLOW_START_NODE_ID)
            return endpoint;
        const raw = String(endpoint);
        return legacy_to_stable.get(raw) || raw;
    };

    const flow_edges = sanitizeWorkflowFlowEdges(
        sorted,
        (norm.flow_edges || []).map((edge) => ({
            ...edge,
            source: map_edge_endpoint(edge.source),
            target: map_edge_endpoint(edge.target),
        }))
    );

    return {
        ...norm,
        nodes,
        flow_edges,
    };
}

/**
 * After a workflow PUT, new bricks receive a server `id` while `prepareForUpdate` strips
 * `_clientLayoutId`. `editor_layout.flow_edges` / `nodes` may still reference the old client
 * node id, so brick→brick incoming edges disappear and `ensureStartEdgesForBricks` adds
 * `Start → new`. Aligns layout references using the pre-save payload (same brick order).
 *
 * @param {Workflow} saved_workflow
 * @param {object} pre_save_item workflow object as dispatched to update (still has `_clientLayoutId`)
 * @returns {Workflow}
 */
export function remapEditorLayoutAfterBrickIdChanges(saved_workflow, pre_save_item) {
    if (!saved_workflow || !pre_save_item)
        return saved_workflow;

    const pre_sorted = sortBricks(pre_save_item.bricks || []);
    const post_sorted = sortBricks(saved_workflow.bricks || []);
    const n = Math.min(pre_sorted.length, post_sorted.length);
    const id_map = {};

    for (let i = 0; i < n; i++) {
        const pre = pre_sorted[i];
        const post = post_sorted[i];
        const old_id = stableBrickNodeId(pre);
        const new_id = stableBrickNodeId(post);
        if (old_id === new_id)
            continue;
        if (pre && pre._clientLayoutId)
            id_map[old_id] = new_id;
    }

    if (Object.keys(id_map).length === 0)
        return saved_workflow;

    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[workflow] Remapped editor_layout brick node ids after save', id_map);
    }

    const norm = normalizeEditorLayout(saved_workflow.editor_layout);
    const map_node = (nid) => {
        const s = String(nid);
        return id_map[s] || s;
    };

    const remapped_nodes = {};
    for (const [k, v] of Object.entries(norm.nodes || {})) {
        const nk = map_node(k);
        remapped_nodes[nk] = v;
    }

    const flow_edges = sanitizeWorkflowFlowEdges(post_sorted, (norm.flow_edges || []).map((e) => ({
        ...e,
        source: map_node(e.source),
        target: map_node(e.target),
    })));

    const next_layout = { ...norm, nodes: remapped_nodes, flow_edges };
    const raw = cloneDeep(saved_workflow);
    raw.editor_layout = next_layout;
    return new Workflow(raw);
}

export function defaultPositionForIndex(index) {
    return {
        x: index * (BRICK_NODE_WIDTH + BRICK_NODE_GAP_X),
        y: 80,
    };
}

/**
 * @param {object} brick
 * @param {object} editor_layout
 * @param {number} index
 * @returns {{ x: number, y: number }}
 */
export function layoutPositionForBrick(brick, editor_layout, index) {
    const norm = normalizeEditorLayout(editor_layout);
    const key = layoutStorageKeyForBrick(brick);
    if (key && norm.nodes[key])
        return { x: Number(norm.nodes[key].x) || 0, y: Number(norm.nodes[key].y) || 0 };
    return defaultPositionForIndex(index);
}

/** @param {import('@xyflow/react').Edge[]} rf_edges */
export function serializeFlowEdges(rf_edges) {
    return (rf_edges || []).map((e) => {
        const base = {
            id: String(e.id),
            source: String(e.source),
            target: String(e.target),
            sourceHandle: e.sourceHandle ? migrateLegacyFlowHandleId(e.sourceHandle) : FLOW_HANDLE_BOTTOM,
            targetHandle: e.targetHandle ? migrateLegacyFlowHandleId(e.targetHandle) : FLOW_HANDLE_TOP,
        };
        const c = normalizeEdgeCondition(e.condition ?? e.data?.condition);
        const tm = normalizeEdgeTiming(e.timing ?? e.data?.timing);
        const label = e.label ?? e.data?.label;
        const priority = Number.isFinite(Number(e.priority ?? e.data?.priority))
            ? Number(e.priority ?? e.data?.priority)
            : 0;
        const is_default = Boolean(e.is_default ?? e.data?.is_default);
        return {
            ...base,
            ...(c ? { condition: c } : {}),
            ...(tm ? { timing: tm } : {}),
            ...(label != null && String(label).trim() !== '' ? { label: String(label).trim() } : {}),
            ...(priority !== 0 ? { priority } : {}),
            ...(is_default ? { is_default: true } : {}),
        };
    });
}

/**
 * Linear chain matching current brick order (minimal edges).
 * @param {unknown[]} sorted_bricks
 */
export function synthesizeLinearFlowEdges(sorted_bricks) {
    const edges = [];
    for (let i = 0; i < sorted_bricks.length - 1; i++) {
        const a = stableBrickNodeId(sorted_bricks[i]);
        const b = stableBrickNodeId(sorted_bricks[i + 1]);
        edges.push({
            id: `e-${a}-${b}`,
            source: a,
            target: b,
            sourceHandle: FLOW_HANDLE_BOTTOM,
            targetHandle: FLOW_HANDLE_TOP,
        });
    }
    return edges;
}

/**
 * Add start → entry brick edges when missing (entries = in-degree 0 in brick-only subgraph).
 * @param {unknown[]} sorted_bricks
 * @param {object[]} brick_edges edges between bricks only
 * @returns {object[]} brick_edges + start edges
 */
export function ensureStartEdgesForBricks(sorted_bricks, brick_edges) {
    const sorted = sortBricks(sorted_bricks || []);
    if (sorted.length === 0)
        return brick_edges || [];
    const ids = sorted.map((b) => stableBrickNodeId(b));
    const id_set = new Set(ids);
    const in_degree = {};
    ids.forEach((id) => {
        in_degree[id] = 0;
    });
    (brick_edges || []).forEach((e) => {
        if (id_set.has(e.source) && id_set.has(e.target))
            in_degree[e.target] += 1;
    });
    const entries = ids.filter((id) => in_degree[id] === 0);
    const out = [...(brick_edges || [])];
    const existing_start = new Set(
        out.filter((e) => e.source === WORKFLOW_START_NODE_ID).map((e) => e.target)
    );
    entries.forEach((tid) => {
        if (existing_start.has(tid))
            return;
        out.push({
            id: `e-${WORKFLOW_START_NODE_ID}-${tid}`,
            source: WORKFLOW_START_NODE_ID,
            target: tid,
            sourceHandle: FLOW_HANDLE_RIGHT,
            targetHandle: FLOW_HANDLE_LEFT,
        });
    });
    return out;
}

/**
 * React Flow keys edges by `id`; duplicate ids (e.g. duplicated rows in `flow_edges`) break reconciliation.
 *
 * @param {object[]|null|undefined} edges
 * @returns {object[]}
 */
export function ensureUniqueFlowEdgeIds(edges) {
    const used = new Set();
    return (edges || []).map((e) => {
        const src = String(e.source ?? '');
        const tgt = String(e.target ?? '');
        let id = e.id != null && String(e.id) !== '' ? String(e.id) : `e-${src}-${tgt}`;
        if (used.has(id)) {
            let n = 1;
            let next = `${id}__${n}`;
            while (used.has(next)) {
                n += 1;
                next = `${id}__${n}`;
            }
            id = next;
        }
        used.add(id);
        return { ...e, id };
    });
}

/**
 * Brick ids with in-degree 0 in the brick-only subgraph (workflow entry nodes).
 *
 * @param {string[]} brick_node_ids
 * @param {object[]} brick_only_edges edges where source and target are both bricks
 * @returns {ReadonlySet<string>}
 */
export function brickEntryNodeIdSet(brick_node_ids, brick_only_edges) {
    const ids = [...brick_node_ids];
    const id_set = new Set(ids);
    const in_degree = {};
    ids.forEach((id) => {
        in_degree[id] = 0;
    });
    (brick_only_edges || []).forEach((e) => {
        const s = String(e.source);
        const t = String(e.target);
        if (id_set.has(s) && id_set.has(t))
            in_degree[t] += 1;
    });
    return new Set(ids.filter((id) => in_degree[id] === 0));
}

/**
 * Drop Start → brick edges whose target is not a graph entry (in-degree 0 among brick edges).
 * Prevents bogus parallel "roots" that break BFS step numbering and clutter the canvas.
 *
 * @param {unknown[]} bricks
 * @param {object[]} flow_edges
 * @returns {object[]}
 */
export function filterSpuriousStartFlowEdges(bricks, flow_edges) {
    const sorted = sortBricks(bricks || []);
    const id_list = sorted.map((b) => stableBrickNodeId(b));
    const node_id_set = new Set(id_list);
    const brick_only = (flow_edges || []).filter(
        (e) => node_id_set.has(String(e.source)) && node_id_set.has(String(e.target))
    );
    const entry_ids = brickEntryNodeIdSet(id_list, brick_only);
    return (flow_edges || []).filter((e) => {
        if (String(e.source) !== WORKFLOW_START_NODE_ID)
            return true;
        const t = String(e.target);
        return node_id_set.has(t) && entry_ids.has(t);
    });
}

/**
 * Signature for deduplicating flow edges (matches Kore WorkflowDag::minimal_flow_edges).
 *
 * @param {object} e
 * @returns {string}
 */
function flowEdgeDedupeSignature(e) {
    const c = e?.condition ?? null;
    const c_key = c && typeof c === 'object' ? JSON.stringify(c) : '';
    const def = e?.is_default ? '1' : '0';
    return `${String(e.source)}|${String(e.target)}|${c_key}|${def}`;
}

/**
 * Drop duplicate edges (same source, target, condition, default flag).
 *
 * @param {object[]|null|undefined} flow_edges
 * @returns {object[]}
 */
export function dedupeFlowEdges(flow_edges) {
    const seen = new Set();
    const out = [];
    (flow_edges || []).forEach((e) => {
        const sig = flowEdgeDedupeSignature(e);
        if (seen.has(sig))
            return;
        seen.add(sig);
        out.push(e);
    });
    return out;
}

/**
 * Ensure each brick resolves to a unique React Flow node id (fixes pos-N collisions after reorder).
 *
 * @param {unknown[]} bricks
 * @returns {unknown[]}
 */
export function ensureUniqueBrickStableIds(bricks) {
    const sorted = sortBricks(bricks || []);
    const used = new Set();
    return sorted.map((brick, index) => {
        if (!brick || typeof brick !== 'object')
            return brick;
        const next = { ...brick };
        let sid = stableBrickNodeId(next);
        if (used.has(sid)) {
            const new_id = typeof crypto !== 'undefined' && crypto.randomUUID
                ? crypto.randomUUID()
                : `cid-${Date.now()}-${index}`;
            next.id = new_id;
            if (next._clientLayoutId === sid)
                delete next._clientLayoutId;
            sid = new_id;
        }
        used.add(sid);
        return next;
    });
}

/**
 * Promote `_clientLayoutId` to persistent `id` before API save (engine keeps brick.id as-is).
 *
 * @param {unknown[]} bricks
 * @returns {unknown[]}
 */
export function persistBrickLayoutIdsForSave(bricks) {
    return ensureUniqueBrickStableIds((bricks || []).map((brick) => {
        if (!brick || typeof brick !== 'object')
            return brick;
        const next = { ...brick };
        if (next._clientLayoutId && !next.id)
            next.id = String(next._clientLayoutId);
        return next;
    }));
}

/**
 * Normalize persisted flow edges: drop orphans/self-loops, dedupe, remove spurious Start links.
 *
 * @param {unknown[]} bricks
 * @param {object[]|null|undefined} flow_edges
 * @returns {object[]}
 */
export function sanitizeWorkflowFlowEdges(bricks, flow_edges) {
    const sorted = ensureUniqueBrickStableIds(bricks || []);
    const id_set = new Set(sorted.map((b) => stableBrickNodeId(b)));
    const filtered = (flow_edges || []).filter((e) => {
        const s = String(e.source);
        const t = String(e.target);
        if (s === t)
            return false;
        if (t === WORKFLOW_START_NODE_ID)
            return false;
        if (s === WORKFLOW_START_NODE_ID)
            return id_set.has(t);
        return id_set.has(s) && id_set.has(t);
    });
    const deduped = dedupeFlowEdges(filtered);
    const healed = filterSpuriousStartFlowEdges(sorted, deduped);
    return ensureUniqueFlowEdgeIds(healed);
}

/**
 * @param {object} e
 * @returns {boolean}
 */
function edge_has_real_condition(e) {
    const c = e?.condition || e?.data?.condition;
    return !!(c && typeof c === 'object' && String(c.path || '').trim() && String(c.op || '').trim());
}

/**
 * Plain = no condition and no default branch marker (matches Kore WorkflowDag::validate_graph).
 *
 * @param {object} e
 * @returns {boolean}
 */
function edge_is_plain_xor_out(e) {
    return !edge_has_real_condition(e) && !e?.is_default && !e?.data?.is_default;
}

/**
 * Same rules as {@see WorkflowDag::coerce_xor_outgoing_edges} (Kore): avoid graph_xor_parallel_mix
 * when one branch has a condition and a sibling was left without condition/default after a reconnect.
 *
 * @param {object[]} flow_edges
 * @param {string[]} brick_node_ids
 * @returns {object[]}
 */
export function coerceXorFanOutFlowEdges(flow_edges, brick_node_ids) {
    const id_set = new Set((brick_node_ids || []).map((id) => String(id)));
    const edges = (flow_edges || []).map((e) => ({ ...e }));
    edges.forEach((e) => {
        if (edge_has_real_condition(e) && e.is_default) {
            delete e.is_default;
        }
        if (e.data && e.data.is_default && edge_has_real_condition(e)) {
            const d = { ...e.data };
            delete d.is_default;
            if (Object.keys(d).length === 0) {
                delete e.data;
            } else {
                e.data = d;
            }
        }
    });
    const by_source = {};
    edges.forEach((e, idx) => {
        const s = String(e.source || '');
        const t = String(e.target || '');
        if (s === WORKFLOW_START_NODE_ID || !id_set.has(s) || !id_set.has(t)) {
            return;
        }
        if (!by_source[s]) {
            by_source[s] = [];
        }
        by_source[s].push(idx);
    });
    Object.values(by_source).forEach((indices) => {
        if (indices.length <= 1) {
            return;
        }
        const plain = [];
        const gated = [];
        indices.forEach((i) => {
            if (edge_is_plain_xor_out(edges[i])) {
                plain.push(i);
            } else {
                gated.push(i);
            }
        });
        if (plain.length === 1 && gated.length > 0) {
            edges[plain[0]].is_default = true;
        }
    });
    return edges;
}

/**
 * Persisted edges if any and coherent with current bricks; otherwise synthetic linear edges.
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 */
export function getMinimalFlowEdges(bricks, editor_layout) {
    const sorted = sortBricks(bricks || []);
    const id_list = sorted.map((b) => stableBrickNodeId(b));
    const node_ids = new Set(id_list);
    const norm = normalizeEditorLayout(editor_layout);
    const sanitized = sanitizeWorkflowFlowEdges(sorted, norm.flow_edges);
    const flow_coerced = coerceXorFanOutFlowEdges(sanitized, id_list);
    let brick_only = flow_coerced.filter((e) => node_ids.has(e.source) && node_ids.has(e.target));
    if (brick_only.length === 0)
        brick_only = synthesizeLinearFlowEdges(sorted);
    brick_only = dedupeFlowEdges(brick_only);
    const entry_ids = brickEntryNodeIdSet(id_list, brick_only);
    const start_edges = flow_coerced.filter(
        (e) =>
            e.source === WORKFLOW_START_NODE_ID &&
            node_ids.has(e.target) &&
            entry_ids.has(String(e.target))
    );
    const merged = [...brick_only];
    const seen = new Set(brick_only.map((e) => flowEdgeDedupeSignature(e)));
    start_edges.forEach((e) => {
        const k = flowEdgeDedupeSignature(e);
        if (!seen.has(k)) {
            seen.add(k);
            merged.push(e);
        }
    });
    return sanitizeWorkflowFlowEdges(sorted, ensureStartEdgesForBricks(sorted, merged));
}

/**
 * Count of incoming edges to this brick in the effective graph (including from {@link WORKFLOW_START_NODE_ID}).
 * Used to show “incoming merge” options in the step inspector when a step has multiple parents.
 *
 * @param {unknown[]} bricks
 * @param {object|null|undefined} editor_layout
 * @param {string} brick_stable_id from {@link stableBrickNodeId}
 * @returns {number}
 */
export function countIncomingFlowParents(bricks, editor_layout, brick_stable_id) {
    const edges = getMinimalFlowEdges(bricks || [], editor_layout);
    const tid = String(brick_stable_id);
    return edges.filter((e) => String(e.target) === tid).length;
}

/**
 * Brick ids reachable from the foreach non-default (iterate) branch without traversing back into the head.
 * Mirrors Kore {@see WorkflowDag::loop_body_brick_ids_for_foreach}.
 *
 * @param {unknown[]} bricks
 * @param {object|null|undefined} editor_layout
 * @param {string} foreach_stable_id
 * @returns {string[]}
 */
export function loopBodyBrickIdsForForeach(bricks, editor_layout, foreach_stable_id) {
    const sorted = sortBricks(bricks || []);
    const id_set = new Set(sorted.map((b) => stableBrickNodeId(b)));
    const edges = getMinimalFlowEdges(bricks || [], editor_layout);
    const outs = edges.filter((e) => String(e.source) === String(foreach_stable_id));
    const iter_edge = outs.find((e) => !e.is_default);
    if (!iter_edge) {
        return [];
    }
    const start_body = String(iter_edge.target);
    if (start_body === String(foreach_stable_id)) {
        return [];
    }
    const visited = new Set();
    const queue = [start_body];
    while (queue.length > 0) {
        const n = queue.shift();
        if (visited.has(n)) {
            continue;
        }
        visited.add(n);
        edges.forEach((e) => {
            if (String(e.source) !== n) {
                return;
            }
            const t = String(e.target);
            if (t === String(foreach_stable_id)) {
                return;
            }
            if (!id_set.has(t)) {
                return;
            }
            queue.push(t);
        });
    }
    return [...visited];
}

/** Dash pattern for edges drawn inside a foreach loop zone (corpus ∪ loop head). */
const FOREACH_LOOP_INTERIOR_STROKE_DASH = '6 4';

/**
 * Keys `source|target` for edges that lie inside a foreach iteration zone (dashed in the graph editor).
 * Zone = loop body nodes plus the foreach brick id, so iterate-entry, body wiring, and tail→head reuse edges match.
 *
 * @param {unknown[]} bricks
 * @param {object|null|undefined} editor_layout
 * @param {object[]} min_edges same topology as {@see getMinimalFlowEdges}
 * @returns {Set<string>}
 */
export function foreachLoopInteriorDashEdgeKeys(bricks, editor_layout, min_edges) {
    const keys = new Set();
    const edges = min_edges || [];
    for (const brick of sortBricks(bricks || [])) {
        if (String(brick.type) !== 'foreach')
            continue;
        const fid = stableBrickNodeId(brick);
        const body = loopBodyBrickIdsForForeach(bricks, editor_layout, fid);
        if (body.length === 0)
            continue;
        const zone = new Set([...body, fid]);
        for (const e of edges) {
            const s = String(e.source);
            const t = String(e.target);
            if (s === WORKFLOW_START_NODE_ID || t === WORKFLOW_START_NODE_ID)
                continue;
            if (zone.has(s) && zone.has(t))
                keys.add(`${s}|${t}`);
        }
    }
    return keys;
}

/**
 * Edges written with the workflow (same normalization as the graph editor after persist):
 * minimal topology from bricks + stored layout, then spurious Start→brick links removed.
 *
 * @param {unknown[]} bricks
 * @param {object|null|undefined} editor_layout
 * @returns {object[]}
 */
export function getPersistedFlowEdgesForWorkflow(bricks, editor_layout) {
    const norm = normalizeEditorLayout(editor_layout);
    const min = getMinimalFlowEdges(bricks || [], norm);
    return filterSpuriousStartFlowEdges(bricks || [], serializeFlowEdges(min));
}

/**
 * @param {object[]} min_edges
 * @param {unknown[]} brick_node_ids — stable ids in current brick set
 */
export function pathOrderFromEdges(brick_node_ids, min_edges) {
    const ids = [...brick_node_ids];
    if (ids.length === 0)
        return [];
    if (ids.length === 1)
        return ids;
    const in_degree = {};
    const out_map = {};
    ids.forEach((id) => {
        in_degree[id] = 0;
        out_map[id] = [];
    });
    (min_edges || []).forEach((e) => {
        if (e.source === WORKFLOW_START_NODE_ID || e.target === WORKFLOW_START_NODE_ID)
            return;
        if (!in_degree.hasOwnProperty(e.source) || !in_degree.hasOwnProperty(e.target))
            return;
        in_degree[e.target] += 1;
        out_map[e.source].push(e.target);
    });
    const starts = ids.filter((id) => in_degree[id] === 0);
    const ends = ids.filter((id) => out_map[id].length === 0);
    if (starts.length !== 1 || ends.length !== 1)
        return null;
    const order = [];
    const seen = new Set();
    let cur = starts[0];
    while (cur != null) {
        if (seen.has(cur))
            return null;
        seen.add(cur);
        order.push(cur);
        const outs = (out_map[cur] || []).slice().sort();
        if (outs.length > 1)
            return null;
        if (outs.length === 0)
            break;
        cur = outs[0];
    }
    if (order.length !== ids.length)
        return null;
    if (order[order.length - 1] !== ends[0])
        return null;
    return order;
}

/**
 * Sort outgoing edges for stable display traversal (matches XOR edge ordering in Kore:
 * priority ascending, then non-default before default, then edge id, then target).
 *
 * @param {object} a
 * @param {object} b
 * @returns {number}
 */
function compareEdgesForFlowDisplay(a, b) {
    const pa = Number(a.priority ?? 0);
    const pb = Number(b.priority ?? 0);
    if (pa !== pb)
        return pa - pb;
    const defa = a.is_default ? 1 : 0;
    const defb = b.is_default ? 1 : 0;
    if (defa !== defb)
        return defa - defb;
    const ida = String(a.id ?? '');
    const idb = String(b.id ?? '');
    if (ida !== idb)
        return ida.localeCompare(idb);
    return String(a.target).localeCompare(String(b.target));
}

/**
 * Multiple outgoing edges from one brick are a gated XOR fan-out (no plain parallel edge).
 * Targets must share the same display step index.
 *
 * @param {object[]} outs
 * @returns {boolean}
 */
export function isGatedXorFanOut(outs) {
    if (!outs || outs.length <= 1)
        return false;
    if (outs.some((e) => !e.condition && !e.is_default))
        return false;
    return outs.some((e) => Boolean(e.condition) || Boolean(e.is_default));
}

/**
 * @param {string[]} ids
 * @param {Set<string>} id_set
 * @param {object[]} min_edges
 * @returns {{ ids: string[], id_set: Set<string>, out_edges: Record<string, object[]>, seeds: string[] }}
 */
function flowGraphContext(ids, id_set, min_edges) {
    const out_edges = {};
    ids.forEach((id) => {
        out_edges[id] = [];
    });
    (min_edges || []).forEach((e) => {
        const s = String(e.source);
        const t = String(e.target);
        if (s === WORKFLOW_START_NODE_ID && id_set.has(t))
            return;
        if (id_set.has(s) && id_set.has(t))
            out_edges[s].push(e);
    });
    const start_targets = (min_edges || [])
        .filter((e) => String(e.source) === WORKFLOW_START_NODE_ID && id_set.has(String(e.target)))
        .map((e) => String(e.target));
    let seeds = [...new Set(start_targets)].filter((tid) => id_set.has(tid));
    seeds.sort((a, b) => a.localeCompare(b));
    if (seeds.length === 0) {
        const in_degree = {};
        ids.forEach((id) => {
            in_degree[id] = 0;
        });
        (min_edges || []).forEach((e) => {
            const s = String(e.source);
            const t = String(e.target);
            if (s === WORKFLOW_START_NODE_ID || t === WORKFLOW_START_NODE_ID)
                return;
            if (id_set.has(s) && id_set.has(t))
                in_degree[t] += 1;
        });
        seeds = ids.filter((id) => in_degree[id] === 0).sort((a, b) => a.localeCompare(b));
    }
    if (seeds.length === 0)
        seeds = [...ids].sort((a, b) => a.localeCompare(b));
    return { ids, id_set, out_edges, seeds };
}

/**
 * 0-based display step index per brick. Conditional (XOR) siblings from the same parent share the same index.
 * Convergent paths use the max of incoming proposals. Unreachable bricks get indices after the main component.
 *
 * @param {string[]} brick_node_ids
 * @param {object[]} min_edges
 * @returns {Record<string, number>}
 */
export function displayStepIndexByBrickId(brick_node_ids, min_edges) {
    const ids = [...brick_node_ids];
    if (ids.length === 0)
        return {};
    if (ids.length === 1)
        return { [ids[0]]: 0 };
    const id_set = new Set(ids);
    const ctx = flowGraphContext(ids, id_set, min_edges);
    const { out_edges, seeds } = ctx;
    const sortEdges = (edges) => [...edges].sort(compareEdgesForFlowDisplay);
    const rank = {};
    seeds.forEach((id) => {
        rank[id] = 0;
    });
    const max_passes = ids.length + 2;
    for (let pass = 0; pass < max_passes; pass++) {
        let changed = false;
        for (const u of ids) {
            if (rank[u] === undefined)
                continue;
            const outs = sortEdges(out_edges[u] || []);
            if (outs.length === 0)
                continue;
            const base = rank[u];
            if (isGatedXorFanOut(outs)) {
                const next_rank = base + 1;
                for (const e of outs) {
                    const v = String(e.target);
                    if (!id_set.has(v))
                        continue;
                    const prev = rank[v];
                    if (prev === undefined || next_rank > prev) {
                        rank[v] = next_rank;
                        if (prev !== next_rank)
                            changed = true;
                    }
                }
            } else if (outs.length === 1) {
                const v = String(outs[0].target);
                if (!id_set.has(v))
                    continue;
                const next_rank = base + 1;
                const prev = rank[v];
                if (prev === undefined || next_rank > prev) {
                    rank[v] = next_rank;
                    if (prev !== next_rank)
                        changed = true;
                }
            } else {
                for (let i = 0; i < outs.length; i++) {
                    const e = outs[i];
                    const v = String(e.target);
                    if (!id_set.has(v))
                        continue;
                    const next_rank = base + 1 + i;
                    const prev = rank[v];
                    if (prev === undefined || next_rank > prev) {
                        rank[v] = next_rank;
                        if (prev !== next_rank)
                            changed = true;
                    }
                }
            }
        }
        if (!changed)
            break;
    }
    let max_r = 0;
    ids.forEach((id) => {
        if (rank[id] !== undefined && rank[id] > max_r)
            max_r = rank[id];
    });
    const missing = ids.filter((id) => rank[id] === undefined).sort((a, b) => a.localeCompare(b));
    missing.forEach((id, i) => {
        rank[id] = max_r + 1 + i;
    });
    return rank;
}

/**
 * BFS from Start through brick edges; traversal order for default layout positions and Start anchoring.
 * Bricks not reachable from Start are appended in stable id sort.
 *
 * @param {string[]} brick_node_ids
 * @param {object[]} min_edges
 * @returns {string[]}
 */
export function displayFlowOrderBrickIds(brick_node_ids, min_edges) {
    const ids = [...brick_node_ids];
    if (ids.length === 0)
        return [];
    if (ids.length === 1)
        return [ids[0]];
    const id_set = new Set(ids);
    const { out_edges, seeds } = flowGraphContext(ids, id_set, min_edges);
    const sortEdges = (edges) => [...edges].sort(compareEdgesForFlowDisplay);
    const order = [];
    const seen = new Set();
    const queue = [...seeds];
    while (queue.length > 0) {
        const id = queue.shift();
        if (seen.has(id))
            continue;
        seen.add(id);
        order.push(id);
        const outs = sortEdges(out_edges[id] || []);
        for (const e of outs) {
            const tid = String(e.target);
            if (id_set.has(tid))
                queue.push(tid);
        }
    }
    const missing = ids.filter((id) => !seen.has(id)).sort((a, b) => a.localeCompare(b));
    missing.forEach((id) => order.push(id));
    return order;
}

/**
 * Brick ids reachable from `fromStableId` by following brick→brick edges forward
 * (excludes `fromStableId`; same graph topology as {@see getMinimalFlowEdges}).
 *
 * @param {Set<string>} brick_id_set
 * @param {object[]} min_edges from {@see getMinimalFlowEdges}
 * @param {string} fromStableId see {@link stableBrickNodeId}
 * @returns {Set<string>}
 */
function reachableDownstreamBrickIds(brick_id_set, min_edges, fromStableId) {
    const from = String(fromStableId);
    if (!brick_id_set.has(from))
        return new Set();
    const reached = new Set();
    const queue = [];
    for (const e of min_edges || []) {
        if (String(e.source) !== from)
            continue;
        const t = String(e.target);
        if (brick_id_set.has(t))
            queue.push(t);
    }
    while (queue.length > 0) {
        const n = queue.shift();
        if (reached.has(n))
            continue;
        reached.add(n);
        for (const e of min_edges || []) {
            if (String(e.source) !== n)
                continue;
            const t = String(e.target);
            if (!brick_id_set.has(t))
                continue;
            if (!reached.has(t))
                queue.push(t);
        }
    }
    return reached;
}

/**
 * Bricks reachable forward from `fromStableId` in the execution graph, ordered like
 * {@see displayFlowOrderBrickIds} (BFS from Start, then unreachable appended).
 *
 * @param {unknown[]} bricks
 * @param {object|null|undefined} editor_layout
 * @param {string} fromStableId node id from {@link stableBrickNodeId}
 * @returns {unknown[]} brick instances in flow order
 */
export function getDownstreamBricksInFlowOrder(bricks, editor_layout, fromStableId) {
    const sorted = sortBricks(bricks || []);
    const id_list = sorted.map((b) => stableBrickNodeId(b));
    const brick_by_id = new Map(id_list.map((id, i) => [id, sorted[i]]));
    const brick_id_set = new Set(id_list);
    const min_edges = getMinimalFlowEdges(bricks, editor_layout);
    const reachable = reachableDownstreamBrickIds(brick_id_set, min_edges, fromStableId);
    const flow_order = displayFlowOrderBrickIds(id_list, min_edges);
    return flow_order
        .filter((id) => reachable.has(id))
        .map((id) => brick_by_id.get(id))
        .filter(Boolean);
}

/**
 * Reassign brick.position from a total order of stable ids.
 * @param {unknown[]} bricks
 * @param {string[]} ordered_stable_ids
 */
export function reorderBricksByStableIds(bricks, ordered_stable_ids) {
    const by_id = new Map(sortBricks(bricks).map((b) => [stableBrickNodeId(b), b]));
    return ordered_stable_ids.map((id, i) => {
        const b = by_id.get(id);
        if (!b)
            return null;
        b.position = i;
        return b;
    }).filter(Boolean);
}

/**
 * When the graph is a single simple path covering all bricks, reorder positions; else keep order.
 * @param {unknown[]} bricks
 * @param {object[]} min_edges
 */
export function applyGraphToBricks(bricks, min_edges) {
    const sorted = sortBricks(bricks || []);
    const ids = sorted.map((b) => stableBrickNodeId(b));
    const order = pathOrderFromEdges(ids, min_edges);
    if (!order)
        return sorted;
    return reorderBricksByStableIds(sorted, order);
}

/**
 * @param {object[]} min_edges
 * @param {{ brickTypes: [string, string][], onInsertBetweenNodes: (source: string, target: string, type: string) => void }} edgeCallbacks
 * @param {Record<string, string>|null|undefined} sourceBrickTypeByNodeId stable brick node id → brick.type (excludes Start)
 * @param {Set<string>|null|undefined} dashEdgeKeys `source|target` keys for dashed “inside foreach loop” styling
 */
export function decorateFlowEdgesForUi(min_edges, edgeCallbacks, sourceBrickTypeByNodeId, dashEdgeKeys) {
    const byId = sourceBrickTypeByNodeId && typeof sourceBrickTypeByNodeId === 'object'
        ? sourceBrickTypeByNodeId
        : {};
    const dash = dashEdgeKeys instanceof Set ? dashEdgeKeys : null;
    return (min_edges || []).map((e) => {
        const src = String(e.source || '');
        const tgt = String(e.target || '');
        const sourceBrickType = src !== '' && src !== WORKFLOW_START_NODE_ID ? (byId[src] ?? null) : null;
        const isLoopInterior = Boolean(dash && dash.has(`${src}|${tgt}`));
        const isFromStart = src === WORKFLOW_START_NODE_ID;
        // Start only exposes a right port (LEFT→RIGHT graph); coerce legacy bottom/top Start edges.
        const sourceHandle = isFromStart
            ? FLOW_HANDLE_RIGHT
            : (migrateLegacyFlowHandleId(e.sourceHandle) || FLOW_HANDLE_BOTTOM);
        const targetHandle = isFromStart
            ? FLOW_HANDLE_LEFT
            : (migrateLegacyFlowHandleId(e.targetHandle) || FLOW_HANDLE_TOP);
        return {
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle,
            targetHandle,
            type: 'insertable',
            deletable: e.source !== WORKFLOW_START_NODE_ID,
            markerEnd: { type: MarkerType.ArrowClosed },
            ...(isLoopInterior ? { style: { strokeDasharray: FOREACH_LOOP_INTERIOR_STROKE_DASH } } : {}),
            ...(e.condition ? { condition: e.condition } : {}),
            ...(e.timing ? { timing: e.timing } : {}),
            ...(e.label ? { label: e.label } : {}),
            ...(e.priority != null && e.priority !== 0 ? { priority: e.priority } : {}),
            ...(e.is_default ? { is_default: true } : {}),
            data: {
                brickTypes: edgeCallbacks.brickTypes,
                onInsertBetweenNodes: edgeCallbacks.onInsertBetweenNodes,
                condition: e.condition,
                timing: e.timing,
                label: e.label,
                priority: e.priority,
                is_default: e.is_default,
                sourceBrickType,
                isForeachLoopInterior: isLoopInterior,
            },
        };
    });
}

/**
 * @param {object} brick
 * @param {string} key
 * @returns {unknown}
 */
function brickOptionValue(brick, key) {
    if (brick && typeof brick.getOption === 'function') {
        const v = brick.getOption(key, undefined);
        if (v != null && v !== '')
            return v;
    }
    const raw = brick?.options?.[key];
    if (raw == null || raw === '')
        return undefined;
    return raw;
}

/**
 * Collect form / email layout / article / salepoint ids referenced by workflow bricks (for preload).
 *
 * @param {unknown[]|null|undefined} bricks
 * @returns {{ formIds: string[], emailLayoutIds: string[], articleIds: string[], salepointIds: string[] }}
 */
export function collectBrickReferencedResourceIds(bricks) {
    const form_ids = new Set();
    const email_layout_ids = new Set();
    const article_ids = new Set();
    const salepoint_ids = new Set();

    for (const brick of sortBricks(bricks || [])) {
        const type = brick.type;
        const opt = (k) => brickOptionValue(brick, k);
        if (type === 'form.show' || type === 'form.handle') {
            const v = opt('form_id');
            if (v != null && v !== '')
                form_ids.add(String(v));
        } else if (type === 'email.send') {
            const v = opt('layout_id');
            if (v != null && v !== '')
                email_layout_ids.add(String(v));
        } else if (type === 'cart.add.article') {
            const sp = opt('salepoint_id');
            const aid = opt('article_id');
            if (sp != null && sp !== '')
                salepoint_ids.add(String(sp));
            if (aid != null && aid !== '')
                article_ids.add(String(aid));
        }
    }

    return {
        formIds: Array.from(form_ids),
        emailLayoutIds: Array.from(email_layout_ids),
        articleIds: Array.from(article_ids),
        salepointIds: Array.from(salepoint_ids),
    };
}

/**
 * Stable signature for effect deps (resource ids referenced by bricks only).
 *
 * @param {unknown[]|null|undefined} bricks
 * @returns {string}
 */
export function workflowBricksResourceIdsSignature(bricks) {
    const { formIds, emailLayoutIds, articleIds, salepointIds } = collectBrickReferencedResourceIds(bricks);
    return [
        [...formIds].sort().join(','),
        [...emailLayoutIds].sort().join(','),
        [...articleIds].sort().join(','),
        [...salepointIds].sort().join(','),
    ].join('::');
}

/**
 * Short human-readable summary under the brick title on the workflow graph.
 * Delegates detail rendering to each brick class (see `getNodeDetails` overrides).
 *
 * @param {object} brick
 * @param {object|null|undefined} ctx
 * @returns {string|null}
 */
export function getBrickFlowNodeDetailText(brick, ctx) {
    if (!brick || !ctx || typeof ctx !== 'object')
        return null;
    try {
        const modelBrick = typeof brick.getNodeDetails === 'function'
            ? brick
            : Brick.make(brick);
        if (typeof modelBrick.getNodeDetails !== 'function')
            return null;

        const details = modelBrick.getNodeDetails(ctx);
        return details == null || String(details).trim() === '' ? null : String(details);
    } catch (_e) {
        return null;
    }
}

/**
 * Primary label for a brick (graph node caption and inspector): translated step title only.
 * Flow order is expressed by graph edges, not by a numeric prefix.
 *
 * @param {unknown[]|null|undefined} _bricks unused (kept for call-site compatibility)
 * @param {object|null|undefined} _editor_layout unused
 * @param {unknown} brick
 * @param {function(string): string} tTitle
 * @returns {string}
 */
export function getBrickFlowNodeDisplayLabel(_bricks, _editor_layout, brick, tTitle) {
    if (!brick || typeof tTitle !== 'function') {
        return '';
    }
    return tTitle(brick.type);
}

/** Font Awesome 5 solid icon name (without fa- prefix) per brick type for graph nodes. */
const BRICK_FLOW_NODE_FA_ICON = {
    'application.create': 'file-signature',
    'cart.add.article': 'shopping-cart',
    'cart.set.user.data': 'id-card',
    'context.set.data': 'sliders-h',
    'email.send': 'envelope',
    'message.show': 'comment',
    'foreach': 'sync-alt',
    'form.show': 'file-alt',
    'form.handle': 'inbox',
    'redirect': 'external-link-alt',
    'wait.approval': 'user-check',
    'wait.payment': 'hourglass-half',
    'user.update': 'user-edit',
};

/**
 * Icon classes for the workflow graph brick card (Font Awesome 5).
 *
 * @param {string|null|undefined} brickType
 * @returns {string}
 */
export function brickFlowNodeIconClassName(brickType) {
    const key = String(brickType || '');
    const icon = BRICK_FLOW_NODE_FA_ICON[key] || 'cube';
    return `fas fa-${icon}`;
}

/**
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 * @param {function(string): string} tTitle
 * @param {{ brickTypes: [string, string][], onInsertBetweenNodes: (source: string, target: string, type: string) => void }} edgeCallbacks
 * @param {{ forms?: object[], emails?: object[], articles?: object[], salepoints?: object[], tickettypes?: object[], lang?: string }|null|undefined} [detailCtx] linked resources + UI lang to resolve option ids into labels
 */
export function buildFlowElements(bricks, editor_layout, tTitle, edgeCallbacks, detailCtx) {
    const sorted = sortBricks(bricks);
    const min_edges = getMinimalFlowEdges(bricks, editor_layout);
    const brick_ids = sorted.map((b) => stableBrickNodeId(b));
    const flow_order = displayFlowOrderBrickIds(brick_ids, min_edges);
    const index_for_id = Object.fromEntries(flow_order.map((id, i) => [id, i]));
    const first_id = flow_order[0];
    const first_brick = sorted.find((b) => stableBrickNodeId(b) === first_id) || sorted[0];

    const nodes = [];

    if (sorted.length > 0) {
        const first_pos = layoutPositionForBrick(
            first_brick,
            editor_layout,
            index_for_id[stableBrickNodeId(first_brick)] ?? 0
        );
        const norm = normalizeEditorLayout(editor_layout);
        const start_stored = norm.nodes[WORKFLOW_START_NODE_ID];
        let start_x;
        let start_y;
        if (
            start_stored
            && Number.isFinite(Number(start_stored.x))
            && Number.isFinite(Number(start_stored.y))
        ) {
            // Prefer ELK / last aligned Start so it stays on the entry-brick axis.
            start_x = Number(start_stored.x);
            start_y = Number(start_stored.y);
        } else {
            // LEFT→RIGHT fallback: Start left of entry brick, vertically centered.
            const brick_h = brickHeightForStartAlignment(first_brick, editor_layout, detailCtx);
            start_x = first_pos.x - WORKFLOW_START_LEFT_GAP - WORKFLOW_START_NODE_BOX;
            start_y = first_pos.y + (brick_h - WORKFLOW_START_NODE_BOX) / 2;
        }
        nodes.push({
            id: WORKFLOW_START_NODE_ID,
            type: 'startNode',
            position: { x: start_x, y: start_y },
            width: WORKFLOW_START_NODE_BOX,
            height: WORKFLOW_START_NODE_BOX,
            data: {},
            draggable: false,
            selectable: true,
        });
    }

    sorted.forEach((brick) => {
        const sid = stableBrickNodeId(brick);
        const index = index_for_id[sid] ?? brick.position ?? 0;
        const detail_text = getBrickFlowNodeDetailText(brick, detailCtx);
        nodes.push({
            id: sid,
            type: 'brickNode',
            position: layoutPositionForBrick(brick, editor_layout, index),
            width: BRICK_NODE_WIDTH,
            data: {
                label: getBrickFlowNodeDisplayLabel(bricks, editor_layout, brick, tTitle),
                brickType: brick.type,
                ...(detail_text ? { detailText: detail_text } : {}),
            },
        });
    });

    const sourceBrickTypeByNodeId = Object.fromEntries(
        sorted.map((b) => [stableBrickNodeId(b), String(b.type || '')])
    );
    const dash_keys = foreachLoopInteriorDashEdgeKeys(sorted, editor_layout, min_edges);
    const edges = decorateFlowEdgesForUi(min_edges, edgeCallbacks, sourceBrickTypeByNodeId, dash_keys);
    return { nodes, edges };
}

/**
 * Create a workflow brick row with catalog inputs/outputs for the editor.
 *
 * @param {string} type
 * @returns {object}
 */
function makeWorkflowBrick(type) {
    return enrichWorkflowBrick(Brick.make({ type }));
}

/**
 * @param {unknown[]} bricks
 * @param {number} insertIndex
 * @param {string} type
 * @returns {unknown[]}
 */
export function insertBrickAt(bricks, insertIndex, type) {
    const list = sortBricks(bricks);
    const brick = makeWorkflowBrick(type);
    brick._clientLayoutId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `cid-${Date.now()}`;
    const safe_index = Math.max(0, Math.min(insertIndex, list.length));
    list.splice(safe_index, 0, brick);
    return list.map((b, i) => {
        b.position = i;
        return b;
    });
}

/**
 * Split edge source→target by inserting a new brick (n8n-style).
 * @returns {{ bricks: unknown[], editor_layout: object }}
 */
export function insertBrickSplittingEdge(bricks, editor_layout, source_id, target_id, type) {
    const sorted = sortBricks(bricks || []);
    const norm = normalizeEditorLayout(editor_layout);
    let min_edges = norm.flow_edges.length > 0 ? [...norm.flow_edges] : synthesizeLinearFlowEdges(sorted);
    min_edges = min_edges.filter((e) => !(e.source === source_id && e.target === target_id));
    const brick = makeWorkflowBrick(type);
    brick._clientLayoutId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `cid-${Date.now()}`;
    const nid = stableBrickNodeId(brick);
    min_edges.push({
        id: `e-${source_id}-${nid}`,
        source: source_id,
        target: nid,
        sourceHandle: FLOW_HANDLE_BOTTOM,
        targetHandle: FLOW_HANDLE_TOP,
    });
    min_edges.push({
        id: `e-${nid}-${target_id}`,
        source: nid,
        target: target_id,
        sourceHandle: FLOW_HANDLE_BOTTOM,
        targetHandle: FLOW_HANDLE_TOP,
    });
    const next_bricks = [...sorted, brick];
    const ordered = pathOrderFromEdges(next_bricks.map((b) => stableBrickNodeId(b)), min_edges);
    const reordered = ordered
        ? reorderBricksByStableIds(next_bricks, ordered)
        : next_bricks.map((b, i) => {
            b.position = i;
            return b;
        });
    min_edges = sanitizeWorkflowFlowEdges(next_bricks, min_edges);
    return {
        bricks: reordered,
        editor_layout: { ...norm, flow_edges: min_edges },
    };
}

/**
 * Brick id to wire as source when appending: a sink (no outgoing brick→brick edge), or one of them.
 * Falls back to last brick in list order if the graph has no sink (e.g. unexpected cycle).
 *
 * @param {unknown[]} sorted_bricks
 * @param {object[]} brick_edges brick→brick edges only (e.g. from getMinimalFlowEdges)
 * @returns {string|null}
 */
function pick_sink_brick_id_for_append(sorted_bricks, brick_edges) {
    const sorted = sortBricks(sorted_bricks || []);
    if (sorted.length === 0)
        return null;
    const ids = sorted.map((b) => stableBrickNodeId(b));
    const id_set = new Set(ids);
    const out_count = {};
    ids.forEach((id) => {
        out_count[id] = 0;
    });
    (brick_edges || []).forEach((e) => {
        const s = String(e.source);
        const t = String(e.target);
        if (s === WORKFLOW_START_NODE_ID || t === WORKFLOW_START_NODE_ID)
            return;
        if (id_set.has(s) && id_set.has(t))
            out_count[s] += 1;
    });
    const sinks = ids.filter((id) => out_count[id] === 0);
    if (sinks.length === 0)
        return stableBrickNodeId(sorted[sorted.length - 1]);
    const flow_order = displayFlowOrderBrickIds(ids, brick_edges || []);
    const sink_set = new Set(sinks);
    for (let i = flow_order.length - 1; i >= 0; i--) {
        const id = flow_order[i];
        if (sink_set.has(id))
            return id;
    }
    sinks.sort((a, b) => a.localeCompare(b));
    return sinks[sinks.length - 1];
}

/**
 * Append a brick: Start → brick if first step; else brick → new from a sink (terminal) node of the graph.
 * @returns {{ bricks: unknown[], editor_layout: object }}
 */
export function appendBrickWithFlowEdge(bricks, editor_layout, type) {
    const sorted = sortBricks(bricks || []);
    const norm = normalizeEditorLayout(editor_layout);
    const brick = makeWorkflowBrick(type);
    brick._clientLayoutId = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `cid-${Date.now()}`;
    const nid = stableBrickNodeId(brick);
    const next_bricks = [...sorted, brick];
    let min_edges = [...getMinimalFlowEdges(sorted, editor_layout)];
    if (sorted.length === 0) {
        min_edges.push({
            id: `e-${WORKFLOW_START_NODE_ID}-${nid}`,
            source: WORKFLOW_START_NODE_ID,
            target: nid,
            sourceHandle: FLOW_HANDLE_RIGHT,
            targetHandle: FLOW_HANDLE_LEFT,
        });
    } else {
        let source_id = pick_sink_brick_id_for_append(sorted, min_edges);
        if (!source_id || source_id === WORKFLOW_START_NODE_ID || !sorted.some((b) => stableBrickNodeId(b) === source_id))
            source_id = stableBrickNodeId(sorted[sorted.length - 1]);
        min_edges.push({
            id: `e-${source_id}-${nid}`,
            source: source_id,
            target: nid,
            sourceHandle: FLOW_HANDLE_BOTTOM,
            targetHandle: FLOW_HANDLE_TOP,
        });
    }
    min_edges = ensureStartEdgesForBricks(next_bricks, min_edges);
    // Never wire Start → newly appended brick when the workflow already has steps (append is never
    // "second entry point"). ensureStartEdges can add it if the brick→new edge is missing or the
    // target id does not match `nid` in the same pass — always strip Start→nid for 2+ bricks.
    if (sorted.length > 0) {
        min_edges = (min_edges || []).filter(
            (e) => !(String(e.source) === WORKFLOW_START_NODE_ID && String(e.target) === nid)
        );
    }
    if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[workflow] appendBrickWithFlowEdge', {
            existing_bricks: sorted.length,
            new_brick_id: nid,
            start_targets: (min_edges || [])
                .filter((e) => String(e.source) === WORKFLOW_START_NODE_ID)
                .map((e) => e.target),
        });
    }
    const ordered = pathOrderFromEdges(next_bricks.map((b) => stableBrickNodeId(b)), min_edges);
    const reordered = ordered
        ? reorderBricksByStableIds(next_bricks, ordered)
        : next_bricks.map((b, i) => {
            b.position = i;
            return b;
        });
    min_edges = sanitizeWorkflowFlowEdges(reordered, min_edges);
    return {
        bricks: reordered,
        editor_layout: { ...norm, flow_edges: min_edges },
    };
}

/**
 * Same graph wiring as `appendBrickWithFlowEdge`, plus the new brick’s top-left in `editor_layout.nodes`
 * (flow coordinates). Used when dropping from the palette onto the canvas.
 *
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 * @param {string} type
 * @param {{ x: number, y: number }} flow_position_tl top-left in flow space
 * @returns {{ bricks: unknown[], editor_layout: object }}
 */
export function appendBrickWithFlowEdgeAtFlowPosition(bricks, editor_layout, type, flow_position_tl) {
    const base = appendBrickWithFlowEdge(bricks, editor_layout, type);
    const old_ids = new Set(sortBricks(bricks || []).map((b) => stableBrickNodeId(b)));
    const new_brick = sortBricks(base.bricks).find((b) => !old_ids.has(stableBrickNodeId(b)));
    if (!new_brick || !flow_position_tl)
        return base;
    const x = Number(flow_position_tl.x) || 0;
    const y = Number(flow_position_tl.y) || 0;
    const next_layout = editorLayoutWithNodePosition(base.editor_layout, new_brick, { x, y });
    return { bricks: base.bricks, editor_layout: next_layout };
}

/**
 * @param {unknown[]} bricks
 * @param {string} node_id
 * @returns {unknown[]}
 */
export function removeBrickByNodeId(bricks, node_id) {
    const list = sortBricks(bricks);
    const filtered = list.filter((b) => stableBrickNodeId(b) !== node_id);
    return filtered.map((b, i) => {
        b.position = i;
        return b;
    });
}

/**
 * @param {object} editor_layout
 * @param {string} removed_storage_key — server brick id
 * @param {string} removed_stable_id — stableBrickNodeId for edge cleanup
 */
export function editorLayoutAfterBrickRemoved(editor_layout, removed_storage_key, removed_stable_id) {
    const norm = normalizeEditorLayout(editor_layout);
    const nodes = { ...norm.nodes };
    if (removed_storage_key)
        delete nodes[removed_storage_key];
    const flow_edges = (norm.flow_edges || []).filter(
        (e) => e.source !== removed_stable_id && e.target !== removed_stable_id
    );
    return { ...norm, nodes, flow_edges };
}

/**
 * @param {object} editor_layout
 * @param {object} brick
 * @param {{ x: number, y: number, width?: number, height?: number }} position
 */
export function editorLayoutWithNodePosition(editor_layout, brick, position) {
    const norm = normalizeEditorLayout(editor_layout);
    const key = layoutStorageKeyForBrick(brick);
    if (!key)
        return norm;
    const prev = norm.nodes[key] && typeof norm.nodes[key] === 'object' ? norm.nodes[key] : {};
    const next = {
        x: position.x,
        y: position.y,
    };
    const width = Number(position.width) > 0 ? Number(position.width) : Number(prev.width);
    const height = Number(position.height) > 0 ? Number(position.height) : Number(prev.height);
    if (width > 0)
        next.width = width;
    if (height > 0)
        next.height = height;
    return {
        ...norm,
        nodes: {
            ...norm.nodes,
            [key]: next,
        },
    };
}

/**
 * Card height used to vertically center Start on the entry brick.
 * Prefers a measured height stored in editor_layout.nodes after auto-layout / drag.
 *
 * @param {object} brick
 * @param {object} editor_layout
 * @param {{ forms?: object[], emails?: object[], articles?: object[], salepoints?: object[], tickettypes?: object[], lang?: string }|null|undefined} [detailCtx]
 * @returns {number}
 */
export function brickHeightForStartAlignment(brick, editor_layout, detailCtx) {
    const key = layoutStorageKeyForBrick(brick);
    const norm = normalizeEditorLayout(editor_layout);
    const stored = key && norm.nodes[key] ? Number(norm.nodes[key].height) : 0;
    if (stored > 0)
        return stored;
    // Compact single-line cards are ~icon column (48px); detail line grows toward LAYOUT_HEIGHT.
    const detail = getBrickFlowNodeDetailText(brick, detailCtx);
    return detail ? BRICK_NODE_LAYOUT_HEIGHT : 50;
}

/**
 * Resolve node box size from React Flow measurements when available.
 *
 * @param {Record<string, { width?: number, height?: number }>|undefined} size_by_id
 * @param {string} node_id
 * @param {{ width: number, height: number }} fallback
 * @returns {{ width: number, height: number }}
 */
function resolve_node_size(size_by_id, node_id, fallback) {
    const raw = size_by_id && size_by_id[node_id];
    const width = Number(raw?.width);
    const height = Number(raw?.height);
    return {
        width: width > 0 ? width : fallback.width,
        height: height > 0 ? height : fallback.height,
    };
}

/**
 * Linear fallback used when ELK fails (same as the previous auto-layout).
 *
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 * @returns {object}
 */
function editor_layout_with_linear_auto_layout(bricks, editor_layout) {
    const norm = normalizeEditorLayout(editor_layout);
    const sorted = sortBricks(bricks || []);
    const ids = sorted.map((b) => stableBrickNodeId(b));
    const min_edges = getMinimalFlowEdges(bricks, editor_layout);
    const graph_order = pathOrderFromEdges(ids, min_edges) || displayFlowOrderBrickIds(ids, min_edges);

    const nodes = { ...norm.nodes };
    graph_order.forEach((node_id, i) => {
        const brick = sorted.find((b) => stableBrickNodeId(b) === node_id);
        if (!brick)
            return;
        const key = layoutStorageKeyForBrick(brick);
        if (key)
            nodes[key] = defaultPositionForIndex(i);
    });
    return { ...norm, nodes };
}

/**
 * Choose React Flow side handles from relative node boxes after layout.
 * For LEFT→RIGHT workflows, prefer right→left whenever the target is clearly to the
 * right (including steep fan-out / fan-in). Vertical ports only when nodes share X.
 *
 * @param {{ x: number, y: number, width: number, height: number }} source_box
 * @param {{ x: number, y: number, width: number, height: number }} target_box
 * @returns {{ sourceHandle: string, targetHandle: string }}
 */
export function pickFlowHandlesForRelativePosition(source_box, target_box) {
    const sx = Number(source_box.x) + Number(source_box.width) / 2;
    const sy = Number(source_box.y) + Number(source_box.height) / 2;
    const tx = Number(target_box.x) + Number(target_box.width) / 2;
    const ty = Number(target_box.y) + Number(target_box.height) / 2;
    const dx = tx - sx;
    const dy = ty - sy;
    // Any meaningful horizontal offset ⇒ side ports (matches reference L→R graphs).
    if (Math.abs(dx) >= 24) {
        if (dx >= 0)
            return { sourceHandle: FLOW_HANDLE_RIGHT, targetHandle: FLOW_HANDLE_LEFT };
        return { sourceHandle: FLOW_HANDLE_LEFT, targetHandle: FLOW_HANDLE_RIGHT };
    }
    if (dy >= 0)
        return { sourceHandle: FLOW_HANDLE_BOTTOM, targetHandle: FLOW_HANDLE_TOP };
    return { sourceHandle: FLOW_HANDLE_TOP, targetHandle: FLOW_HANDLE_BOTTOM };
}

/**
 * Snap brick positions with ELK layered (LEFT→RIGHT). Updates `editor_layout.nodes`
 * and remaps edge handles to match geometry (e.g. side-by-side → right→left).
 * Does not change edge topology / conditions / bricks.
 *
 * @param {unknown[]} bricks
 * @param {object} editor_layout
 * @param {{ sizeById?: Record<string, { width?: number, height?: number }> }} [options]
 * @returns {Promise<object>} normalized editor_layout with updated `nodes` (+ handle sides)
 */
export async function editorLayoutWithAutoLayout(bricks, editor_layout, options = {}) {
    const norm = normalizeEditorLayout(editor_layout);
    const sorted = sortBricks(bricks || []);
    if (sorted.length === 0)
        return norm;

    const size_by_id = options && typeof options.sizeById === 'object' && options.sizeById
        ? options.sizeById
        : {};
    const min_edges = getMinimalFlowEdges(bricks, editor_layout);

    const start_size = resolve_node_size(size_by_id, WORKFLOW_START_NODE_ID, {
        width: WORKFLOW_START_NODE_BOX,
        height: WORKFLOW_START_NODE_BOX,
    });
    const size_by_node_id = {
        [WORKFLOW_START_NODE_ID]: start_size,
    };
    const elk_nodes = [
        {
            id: WORKFLOW_START_NODE_ID,
            width: start_size.width,
            height: start_size.height,
        },
    ];
    sorted.forEach((brick) => {
        const id = stableBrickNodeId(brick);
        const size = resolve_node_size(size_by_id, id, {
            width: BRICK_NODE_WIDTH,
            height: BRICK_NODE_LAYOUT_HEIGHT,
        });
        size_by_node_id[id] = size;
        elk_nodes.push({
            id,
            width: size.width,
            height: size.height,
        });
    });

    const elk_edges = (min_edges || []).map((e) => ({
        id: e.id,
        source: String(e.source),
        target: String(e.target),
    }));

    let laid_by_id;
    try {
        laid_by_id = await computeElkLayeredPositions({
            nodes: elk_nodes,
            edges: elk_edges,
        });
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('[workflow] ELK auto-layout failed, falling back to linear', err);
        }
        return editor_layout_with_linear_auto_layout(bricks, editor_layout);
    }

    const nodes = { ...norm.nodes };
    sorted.forEach((brick) => {
        const id = stableBrickNodeId(brick);
        const key = layoutStorageKeyForBrick(brick);
        const laid = laid_by_id[id];
        if (!key || !laid)
            return;
        const size = size_by_node_id[id] || {
            width: BRICK_NODE_WIDTH,
            height: BRICK_NODE_LAYOUT_HEIGHT,
        };
        nodes[key] = {
            x: Number(laid.x) || 0,
            y: Number(laid.y) || 0,
            width: size.width,
            height: size.height,
        };
    });

    // Persist Start from ELK so it stays on the same axis as the entry brick
    // (same sizes were used for both). buildFlowElements will prefer this.
    const start_laid = laid_by_id[WORKFLOW_START_NODE_ID];
    if (start_laid) {
        nodes[WORKFLOW_START_NODE_ID] = {
            x: Number(start_laid.x) || 0,
            y: Number(start_laid.y) || 0,
            width: start_size.width,
            height: start_size.height,
        };
    }

    const box_for_node_id = (node_id) => {
        const laid = laid_by_id[String(node_id)];
        const size = size_by_node_id[String(node_id)] || {
            width: BRICK_NODE_WIDTH,
            height: BRICK_NODE_LAYOUT_HEIGHT,
        };
        if (!laid)
            return null;
        return {
            x: Number(laid.x) || 0,
            y: Number(laid.y) || 0,
            width: size.width,
            height: size.height,
        };
    };

    const flow_edges = (min_edges || []).map((edge) => {
        const source_box = box_for_node_id(edge.source);
        const target_box = box_for_node_id(edge.target);
        if (!source_box || !target_box)
            return { ...edge };
        const handles = pickFlowHandlesForRelativePosition(source_box, target_box);
        return {
            ...edge,
            sourceHandle: handles.sourceHandle,
            targetHandle: handles.targetHandle,
        };
    });

    return { ...norm, nodes, flow_edges };
}
